import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Modal,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { HexColorPicker } from 'react-colorful';

import { useStyles as useBrandingModalStyles } from '../../../BrandingModeModals/styles';
import BrandingsService from '@services/BrandingsService';
import { IColorScheme } from '@interfaces/rooms';

const DEFAULT_MAP_COLORS: Required<IColorScheme> = {
  primaryColor: '#000000',
  secondaryColor: '#333333',
  outlineColor: '#333333',
};

const isValidHexColor = (value: string) => {
  return /^#[0-9a-f]{6}$/i.test(value.trim());
};

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  textFieldSx?: Record<string, unknown>;
}

const ColorField: React.FC<ColorFieldProps> = ({
  label,
  value,
  onChange,
  helperText,
  error,
  disabled,
  textFieldSx,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const normalized = value?.trim() || '#000000';

  return (
    <>
      <TextField
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        error={error}
        helperText={helperText}
        disabled={disabled}
        fullWidth
        InputLabelProps={{
          sx: {
            color: 'rgba(255, 255, 255, 0.72)',
            '&.Mui-focused': { color: 'rgba(255, 255, 255, 0.88)' },
          },
        }}
        sx={textFieldSx}
        inputProps={{ inputMode: 'text' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                disabled={disabled}
                aria-label={`${label} picker`}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: isValidHexColor(normalized)
                      ? normalized
                      : '#000000',
                    border: '1px solid rgba(255,255,255,0.18)',
                  }}
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(25, 25, 30, 0.98)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(12px)',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <HexColorPicker
            color={isValidHexColor(normalized) ? normalized : '#000000'}
            onChange={onChange}
          />
        </Box>
      </Popover>
    </>
  );
};

interface MapColorsModalProps {
  open: boolean;
  activeBrandingId: string | undefined;
  mapId: string | undefined;
  onClose: () => void;
}

const MapColorsModal: React.FC<MapColorsModalProps> = ({
  open,
  activeBrandingId,
  mapId,
  onClose,
}) => {
  const { t: translate } = useTranslation();
  const brandingModalClasses = useBrandingModalStyles();

  const [mapColors, setMapColors] =
    useState<Required<IColorScheme>>(DEFAULT_MAP_COLORS);
  const [mapColorsError, setMapColorsError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;

    setMapColors(DEFAULT_MAP_COLORS);
    setMapColorsError(
      activeBrandingId ? null : 'No active branding. Select a branding first.',
    );

    if (!activeBrandingId || !mapId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const loadColors = async () => {
      try {
        const res = await BrandingsService.getBrandings({ mapId });
        if (cancelled) {
          return;
        }
        const active = res.brandings?.find(
          (b) => b.brandingId === activeBrandingId,
        );
        if (active?.brandingColors) {
          setMapColors({
            primaryColor:
              active.brandingColors.primaryColor ||
              DEFAULT_MAP_COLORS.primaryColor,
            secondaryColor:
              active.brandingColors.secondaryColor ||
              DEFAULT_MAP_COLORS.secondaryColor,
            outlineColor:
              active.brandingColors.outlineColor ||
              DEFAULT_MAP_COLORS.outlineColor,
          });
        }
      } catch {
        /* stay with defaults */
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadColors();

    return () => {
      cancelled = true;
    };
  }, [open, activeBrandingId, mapId]);

  const handleClose = () => {
    if (isSaving) {
      return;
    }
    onClose();
  };

  const handleModalClose = (
    _event: unknown,
    reason?: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    handleClose();
  };

  const handleColorChange =
    (key: keyof IColorScheme) => (value: string) => {
      setMapColors((prev) => ({ ...prev, [key]: value }));
      setMapColorsError(null);
    };

  const handleSave = async () => {
    if (!activeBrandingId) {
      setMapColorsError('No active branding. Select a branding first.');
      return;
    }

    const colorsToSave: Required<IColorScheme> = {
      primaryColor: mapColors.primaryColor.trim(),
      secondaryColor: mapColors.secondaryColor.trim(),
      outlineColor: mapColors.outlineColor.trim(),
    };

    if (
      !isValidHexColor(colorsToSave.primaryColor) ||
      !isValidHexColor(colorsToSave.secondaryColor) ||
      !isValidHexColor(colorsToSave.outlineColor)
    ) {
      setMapColorsError('Please use HEX colors in format #RRGGBB.');
      return;
    }

    setIsSaving(true);
    try {
      await BrandingsService.updateBranding(activeBrandingId, {
        brandingColors: colorsToSave,
      });
      onClose();
    } catch {
      setMapColorsError('Failed to save colors. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const disabled = isSaving || isLoading;

  return (
    <Modal open={open} onClose={handleModalClose} sx={brandingModalClasses.modalRoot}>
      <Box
        sx={[
          brandingModalClasses.container,
          { width: 620, maxHeight: 'unset', overflow: 'visible' },
        ]}
      >
        <Box sx={brandingModalClasses.header}>
          <Typography sx={brandingModalClasses.title}>
            {translate('adminToolbox.mapColors', 'Map Colors')}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={brandingModalClasses.closeButton}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ padding: '6px 16px 16px' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 6,
              alignItems: 'start',
            }}
          >
            <Stack spacing={1} sx={{ width: '100%' }}>
              <ColorField
                label={translate('adminToolbox.primaryColor', 'Primary Color')}
                value={mapColors.primaryColor}
                onChange={handleColorChange('primaryColor')}
                error={!!mapColorsError}
                disabled={disabled}
                textFieldSx={brandingModalClasses.textField}
                helperText={mapColorsError || undefined}
              />
            </Stack>
            <Stack spacing={1} sx={{ width: '100%' }}>
              <ColorField
                label={translate(
                  'adminToolbox.secondaryColor',
                  'Secondary Color',
                )}
                value={mapColors.secondaryColor}
                onChange={handleColorChange('secondaryColor')}
                error={!!mapColorsError}
                disabled={disabled}
                textFieldSx={brandingModalClasses.textField}
                helperText={mapColorsError || undefined}
              />
            </Stack>
            <Stack spacing={1} sx={{ width: '100%' }}>
              <ColorField
                label={translate('adminToolbox.outlineColor', 'Outline Color')}
                value={mapColors.outlineColor}
                onChange={handleColorChange('outlineColor')}
                error={!!mapColorsError}
                disabled={disabled}
                textFieldSx={brandingModalClasses.textField}
                helperText={mapColorsError || undefined}
              />
            </Stack>
          </Box>

          <Box
            sx={{ ...brandingModalClasses.nameFooter, padding: '12px 0 0' }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={disabled}
              sx={brandingModalClasses.cancelButton}
            >
              {translate('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={disabled}
              sx={brandingModalClasses.continueButton}
            >
              {translate('common.save', { defaultValue: 'Save' })}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default MapColorsModal;